/**
 * Asset detail page (`/assets/:assetId`).
 *
 * The "drill into one device" surface. Goals:
 *   - Header reads like a luggage tag: make/model · size · serial · status.
 *   - "Open in Drive" is always one click away when we have a mirrored
 *     test report — that's the single biggest field-tech ask.
 *   - Tabs are: Overview, Tests, History, Attachments. Each one is
 *     self-contained and lazily fetches only when its tab is visible.
 *   - History is a *merged timeline*: status changes + test results +
 *     activity events, all in one chronological list, no toggling.
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BackLink } from "@/components/back-link";
import { TaskCreateButton } from "@/components/task-create-dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, attachmentDownloadUrl } from "@/lib/api";
import { photoCaption } from "@/lib/photo-label";
import { openExternalLink } from "@/lib/standalone";
import { AttachmentPreviewDialog } from "@/components/attachment-preview-dialog";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FromTheFieldSection } from "@/components/field-add/field-add-office";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ASSEMBLY_TYPES } from "@/components/backflow-device-fields";
import { SERVICE_TYPES } from "@/components/add-device-to-site-modal";
import {
    assetSpecsFormValues,
    buildAssetSpecsPatch,
    INSTALL_SETTING_OPTIONS,
    isEmptyPatch,
    type AssetSpecsFormValues,
} from "./assetSpecsPatch";
import { InfoButton, type InfoTopic } from "@/components/info-button";

/** ⓘ for "Installed" — what it is for, in plain words. */
const INSTALLED_HELP: InfoTopic = {
    title: "Where the device is installed",
    lines: [
        "Pick where this device sits: inside the building above ground, inside below ground, or outside.",
        "Some customers require a set pipe when we replace a device 2 inches or smaller. Inside above ground gets copper. Outside with any PVC gets Sch 80 PVC.",
        "If this is left as Not known, a replacement quote asks the office to pick the pipe.",
    ],
};
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DataTable, useSortedRows, type Column } from "@/components/ui/data-table";
import { SortScopeNote } from "@/components/ui/sort-scope-note";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, formatDate, formatDateOnly, formatDateTime, statusColor } from "@/lib/utils";
import { dateSortValue } from "@/lib/table-sort";
import { StatTile } from "@/components/ui/stat-tile";
import { DEFAULT_VISIBLE_ROWS, ShowMoreFooter } from "@/components/ui/show-more-footer";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { useRequiredAssetFields } from "@/components/assets/DynamicAssetFields";
import { ModelTapList } from "@/components/model-tap-list";
import { DeviceCheckCard } from "@/components/device-check-card";
import {
    ArrowLeft,
    MapPin,
    Wrench,
    ClipboardCheck,
    Paperclip,
    History,
    ExternalLink,
    Building2,
    Hash,
    Droplets,
    AlertCircle,
    Calendar,
    Plus,
    Pencil,
} from "lucide-react";
import { testStateLabel } from "@/lib/test-state";
import { ProofChip } from "@/components/proof/proof-chip";
import type { Proof } from "@/lib/proof";
import { DeviceProofControls, TestProofChip, useDeviceProof, type DeviceProof } from "./device-proof";

// ── Types matched to /api/assets/:assetId + extensions ───────────────

interface AssetDetail {
    id: string;
    site_id: string;
    name: string;
    asset_kind: string | null;
    external_key: string | null;
    notes: string | null;
    size_label: string | null;
    serial_number: string | null;
    manufacturer: string | null;
    model: string | null;
    assembly_type: string | null;
    service_type: string | null;
    service_type_other: string | null;
    meter_number: string | null;
    location_description: string | null;
    location_lat: number | null;
    location_lng: number | null;
    in_vault: boolean | null;
    /** Migration 701: inside_above_ground | inside_below_ground | outside, or null. */
    install_setting?: string | null;
    is_fireline: boolean;
    is_detector_assembly: boolean;
    hazard_classification: "health" | "non_health" | null;
    hazard_description: string | null;
    protected_service_category: "irrigation" | "co2_soda" | "fire_sprinkler" | "point_of_service" | "other" | null;
    protected_service_other: string | null;
    /**
     * Generalized Residential / Commercial classification
     * (migration 196). Replaces the McKinney-only
     * `mckinney_occupancy_type` column. Both columns are written in
     * lockstep by the asset POST/PATCH routes during the cutover.
     */
    occupancy_type: "Residential" | "Commercial" | null;
    status: string;
    /** Non-null renders the amber "verify on site" chip (authority-list reconcile). */
    needs_verification_reason: string | null;
    last_inspection_at: string | null;
    next_due_date: string | null;
    trade_id: string | null;
    trade_code: string | null;
    trade_name: string | null;
    customer_id: string;
    customer_name: string | null;
    site_name: string | null;
    site_address_line: string | null;
    site_city: string | null;
    site_region: string | null;
    site_postal_code: string | null;
    created_at: string;
    updated_at: string;
    /**
     * USC FCCCHR approval status for this device (backflow assets only).
     * Populated by the server-side lookup against `usc_approved_assemblies`
     * when manufacturer + model + size_label are all set. `null` means we
     * could not match this device against the current USC list, or the
     * importer hasn't run yet.
     */
    usc_status?: UscInstallStatus | null;
    usc_manual_edition?: number | null;
    usc_lead_free?: boolean | null;
    usc_spare_parts_only?: boolean | null;
    usc_orientations?: string[] | null;
    usc_approved_on?: string | null;
    usc_renewed_on?: string | null;
    usc_list_edition_date?: string | null;
}

type UscInstallStatus = "installable" | "spare_parts_only" | "expired_renewal" | "legacy_edition" | "unknown";

interface ActivityResponse {
    events: Array<{
        id: string;
        action: string;
        detail: Record<string, unknown> | null;
        created_at: string;
    }>;
    linked_jobs: Array<{
        id: string;
        title: string | null;
        status: string;
        created_at: string;
        required_action: string | null;
    }>;
    tests: Array<{
        id: string;
        kind: "backflow" | "fire_safety";
        performed_at: string;
        overall_result: string | null;
        job_id: string | null;
        /** The job's display number, so the Tests tab can name it. */
        job_number?: string | null;
        performed_by: string | null;
        /** For retests: the failing test row this run is verifying. */
        original_test_id?: string | null;
        reason_for_test?: string | null;
        /**
         * Origin marker derived server-side from `remarks`. See
         * trade-panel.tsx TestRow for the full glossary.
         */
        source?: "native" | "st_backfill" | "historical_pdf" | "vepo";
    }>;
    repairs?: Array<{
        id: string;
        asset_id: string;
        job_id: string | null;
        /** The failing test this repair closes out. */
        original_test_id?: string | null;
        performed_at: string;
        repair_notes: string | null;
        repairs_confirmed?: unknown;
    }>;
    status_history: Array<{
        id: string;
        from_status: string | null;
        to_status: string;
        reason: string | null;
        changed_by: string | null;
        changed_by_name: string | null;
        created_at: string;
    }>;
}

interface AttachmentRow {
    id: string;
    file_name: string;
    content_type: string | null;
    category: string | null;
    storage_uri: string | null;
    google_drive_file_id: string | null;
    google_drive_path: string | null;
    uploaded_by_user_id: string | null;
    job_id: string | null;
    job_title: string | null;
    source: "office" | "field";
    created_at: string;
    /** Photos only: `2" Double Check · SN 40613G · Tag photo` (server-built from this device). */
    photo_label?: string | null;
}

const TRADE_LABEL: Record<string, string> = {
    backflow: "Backflow",
    fire_ext: "Fire Ext",
    underground_fire: "U-Fire",
    plumbing: "Plumbing",
    hvac: "HVAC",
};

const ASSEMBLY_LABEL: Record<string, string> = {
    rpz: "RPZ",
    dc: "DC",
    dcva: "DCVA",
    dcda: "DCDA",
    rpda: "RPDA",
    pvb: "PVB",
    svb: "SVB",
    dcda_ii: "DCDA-II",
    rpda_ii: "RPDA-II",
};

// ── USC FCCCHR compliance badge ──────────────────────────────────────
//
// USC publishes a list of backflow assemblies approved for installation
// under the current Manual edition (10th, as of 2026). Devices listed
// under earlier editions (5-9) remain in service but are NOT approved
// for new install per company policy. The server attaches a derived
// `usc_status` on backflow asset GET responses; we render it as a single
// chip alongside the device status so the office and techs can see at a
// glance whether the device is current/legacy/spare-parts. No badge
// renders when `usc_status` is undefined/null (non-backflow assets,
// missing make/model/size, or pre-import data).
const USC_BADGE_STYLES: Record<UscInstallStatus, string> = {
    installable:
        "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800",
    spare_parts_only:
        "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-800",
    expired_renewal:
        "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-800",
    legacy_edition:
        "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-800",
    unknown: "bg-muted text-foreground/80 border-border",
};

function uscBadgeLabel(status: UscInstallStatus, manualEdition: number | null | undefined): string {
    const editionSuffix = manualEdition ? ` · Manual ${manualEdition}` : "";
    switch (status) {
        case "installable":
            return `USC Approved${editionSuffix}`;
        case "spare_parts_only":
            return `USC: Spare parts only${editionSuffix}`;
        case "expired_renewal":
            return `USC: Renewal expired${editionSuffix}`;
        case "legacy_edition":
            return `USC: Cannot install${editionSuffix}`;
        case "unknown":
        default:
            return "USC: Status unknown";
    }
}

function UscBadge({
    status,
    manualEdition,
    leadFree,
    renewedOn,
    listEditionDate,
}: {
    status: UscInstallStatus | null | undefined;
    manualEdition?: number | null;
    leadFree?: boolean | null;
    renewedOn?: string | null;
    listEditionDate?: string | null;
}) {
    if (!status) return null;
    const tooltipBits: string[] = [];
    if (renewedOn) tooltipBits.push(`Renewed ${renewedOn}`);
    if (leadFree !== null && leadFree !== undefined) {
        tooltipBits.push(leadFree ? "Lead-free (≤0.25% Pb)" : "Not lead-free");
    }
    if (listEditionDate) tooltipBits.push(`USC list ${listEditionDate}`);
    tooltipBits.push(
        "USC FCCCHR — the University of Southern California cross-connection foundation whose approved-assembly list installs follow",
    );
    return (
        <Badge
            variant="outline"
            title={tooltipBits.join(" · ")}
            className={cn("text-[10px] uppercase font-semibold", USC_BADGE_STYLES[status])}
        >
            {uscBadgeLabel(status, manualEdition)}
        </Badge>
    );
}

// ── Page ─────────────────────────────────────────────────────────────

export default function AssetDetailPage() {
    const { assetId } = useParams<{ assetId: string }>();

    const assetQ = useQuery({
        queryKey: ["asset", assetId],
        queryFn: () => api.get<{ asset: AssetDetail }>(`/api/assets/${assetId}`),
        enabled: !!assetId,
    });
    // Proof chips (trust audit Phase 2): only fetched with the person's switch on.
    const dp = useDeviceProof(assetId);

    if (assetQ.isLoading || !assetId) {
        return <AssetDetailSkeleton />;
    }
    if (!assetQ.data) {
        return (
            <div className="p-8 text-center text-foreground/80">
                Asset not found.{" "}
                <BackLink fallback="/customers" fallbackLabel="Back to customers" className="text-primary underline" />
            </div>
        );
    }

    const a = assetQ.data.asset;
    const headerLine = [
        a.manufacturer,
        a.model,
        a.size_label,
        a.assembly_type ? (ASSEMBLY_LABEL[a.assembly_type] ?? a.assembly_type.toUpperCase()) : null,
    ]
        .filter(Boolean)
        .join(" · ");

    return (
        <div className="mx-auto max-w-6xl px-4 py-6 space-y-6">
            {/* ── Identity card (breadcrumb + header)  ─────────────
                Fused into a single solid card so the title doesn't
                fight the hexagon backdrop for legibility. */}
            <Card>
                <CardContent className="p-5 space-y-3">
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                        <Link
                            to={`/sites/${a.site_id}`}
                            className="text-foreground/90 hover:text-primary flex items-center gap-1 font-medium"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" />
                            {a.site_name ?? "Location"}
                        </Link>
                        {a.customer_name && (
                            <>
                                <span className="text-foreground/60">·</span>
                                <Link
                                    to={`/customers/${a.customer_id}`}
                                    className="text-foreground/85 hover:text-primary font-medium"
                                >
                                    {a.customer_name}
                                </Link>
                            </>
                        )}
                    </div>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="space-y-2 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="text-2xl font-bold flex items-center gap-2 text-foreground">
                                    <Wrench className="h-5 w-5 text-foreground/85" />
                                    {a.name}
                                </h1>
                                {a.trade_code && (
                                    <Badge
                                        variant="outline"
                                        className="text-[10px] uppercase bg-primary/10 border-primary/40 text-primary font-semibold"
                                    >
                                        {TRADE_LABEL[a.trade_code] ?? a.trade_name ?? a.trade_code}
                                    </Badge>
                                )}
                                <Badge
                                    variant="outline"
                                    className={cn("text-[10px] uppercase font-semibold", statusColor(a.status))}
                                >
                                    {a.status?.replace(/_/g, " ")}
                                </Badge>
                                <UscBadge
                                    status={a.usc_status}
                                    manualEdition={a.usc_manual_edition}
                                    leadFree={a.usc_lead_free}
                                    renewedOn={a.usc_renewed_on}
                                    listEditionDate={a.usc_list_edition_date}
                                />
                                {a.needs_verification_reason && (
                                    <Badge
                                        variant="outline"
                                        className="text-[10px] border-amber-500/40 text-amber-600 bg-amber-500/10"
                                        title={a.needs_verification_reason}
                                    >
                                        {a.needs_verification_reason}
                                    </Badge>
                                )}
                            </div>
                            {headerLine && <div className="text-sm font-medium text-foreground/85">{headerLine}</div>}
                            <div className="flex flex-wrap items-center gap-3 text-xs text-foreground/85 mt-2">
                                {a.serial_number && (
                                    <span className="inline-flex items-center gap-1">
                                        <Hash className="h-3 w-3" />
                                        <span className="font-mono">{a.serial_number}</span>
                                    </span>
                                )}
                                {a.site_address_line && (
                                    <span className="inline-flex items-center gap-1">
                                        <MapPin className="h-3 w-3" />
                                        {[a.site_address_line, a.site_city, a.site_region].filter(Boolean).join(", ")}
                                    </span>
                                )}
                                {a.location_description && (
                                    <span className="inline-flex items-center gap-1">
                                        <Building2 className="h-3 w-3" />
                                        {a.location_description}
                                    </span>
                                )}
                            </div>
                        </div>

                        <AssetHeaderActions asset={a} />
                    </div>

                    {/* Next-due / last-inspection at-a-glance row */}
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <EditableDueChipTile assetId={a.id} date={a.next_due_date} proof={dp.proof?.due ?? null} />
                        <DueChipTile
                            label="Last inspection"
                            date={a.last_inspection_at}
                            icon={<ClipboardCheck className="h-3 w-3" />}
                            neutral
                        />
                        {(a.service_type || a.service_type_other) && (
                            <div className="rounded-lg border bg-card px-3 py-2">
                                <div className="text-[10px] uppercase tracking-wide text-foreground/80 flex items-center gap-1">
                                    <Droplets className="h-3 w-3" /> Serves
                                </div>
                                <div className="text-sm mt-0.5">
                                    {a.service_type ? formatServes(a.service_type) : a.service_type_other}
                                </div>
                            </div>
                        )}
                    </div>
                    {dp.allowed && <DeviceProofControls on={dp.on} set={dp.set} />}
                </CardContent>
            </Card>

            {/* ── Device check (11/2): proven against the tag? With the proof. ── */}
            <DeviceCheckCard assetId={a.id} />

            {/* ── Add from the field: device notes and photos techs added.
                Never writes over the device's Notes. ── */}
            <FromTheFieldSection kind="device" id={a.id} />

            {/* ── Tabs ────────────────────────────────────────────── */}
            <AssetTabs assetId={a.id} asset={a} proof={dp.proof} />
        </div>
    );
}

// ── Header actions ───────────────────────────────────────────────────

function AssetHeaderActions({ asset }: { asset: AssetDetail }) {
    const qc = useQueryClient();
    // Fetch one attachments page so we can wire "Open in Drive"
    // straight to the most-recent mirrored report. The page-2 fetch
    // is owned by the Attachments tab so we share its cache.
    const attQ = useQuery({
        queryKey: ["asset-attachments", asset.id],
        queryFn: () => api.get<{ attachments: AttachmentRow[] }>(`/api/assets/${asset.id}/attachments?limit=20`),
        enabled: !!asset.id,
        staleTime: 60_000,
    });

    const mostRecentDrive = attQ.data?.attachments.find((r) => r.google_drive_file_id);
    const driveUrl = mostRecentDrive?.google_drive_file_id
        ? `https://drive.google.com/file/d/${mostRecentDrive.google_drive_file_id}/view`
        : null;

    const retire = useMutation({
        mutationFn: () =>
            api.patch(`/api/assets/${asset.id}`, {
                status: "retired",
                statusReason: "Manually retired from asset detail",
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["asset", asset.id] });
            qc.invalidateQueries({ queryKey: ["site-summary", asset.site_id] });
            toast.success("Device retired");
        },
        onError: () => toast.error("Couldn't retire device"),
    });

    return (
        <div className="flex flex-wrap items-center gap-2 shrink-0">
            <TaskCreateButton
                prefill={{
                    title: `${asset.serial_number ? `Device ${asset.serial_number}` : asset.name} — `,
                    links: [
                        {
                            entityType: "asset",
                            entityId: asset.id,
                            label: asset.serial_number ? `Device ${asset.serial_number}` : asset.name,
                        },
                        ...(asset.customer_id
                            ? [{ entityType: "customer", entityId: asset.customer_id, label: "Customer" }]
                            : []),
                    ],
                }}
            />
            {driveUrl && (
                <Button size="sm" variant="outline" onClick={() => openExternalLink(driveUrl)}>
                    <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open in Drive
                </Button>
            )}
            <Link
                to={`/jobs/new?customerId=${asset.customer_id}&siteId=${asset.site_id}&assetId=${asset.id}`}
                className={cn(buttonVariants({ size: "sm" }))}
            >
                <Plus className="h-3.5 w-3.5 mr-1" /> New job
            </Link>
            {asset.manufacturer && asset.model && asset.assembly_type && (
                <Link
                    to={
                        `/pricebook/repair-finder?assembly_type=${encodeURIComponent(asset.assembly_type)}` +
                        `&manufacturer=${encodeURIComponent(asset.manufacturer)}` +
                        `&model=${encodeURIComponent(asset.model)}` +
                        `&size_label=${encodeURIComponent(asset.size_label ?? "")}` +
                        // Who it's for: with the customer the "Find what fits" cards
                        // price through the engine (#1459) instead of showing the
                        // flat-rate parts price.
                        `&customer_id=${encodeURIComponent(asset.customer_id ?? "")}` +
                        `&site_id=${encodeURIComponent(asset.site_id ?? "")}` +
                        `&asset_id=${encodeURIComponent(asset.id)}` +
                        `&serial=${encodeURIComponent(asset.serial_number ?? "")}` +
                        `&site=${encodeURIComponent(asset.site_name ?? "")}` +
                        `&customer=${encodeURIComponent(asset.customer_name ?? "")}`
                    }
                    className={cn(buttonVariants({ size: "sm", variant: "outline" }))}
                >
                    <Wrench className="h-3.5 w-3.5 mr-1" /> Find repair
                </Link>
            )}
            {asset.status !== "retired" && (
                <Button
                    size="sm"
                    variant="outline"
                    className="text-destructive hover:text-destructive"
                    onClick={async () => {
                        if (
                            await confirmDialog(
                                `Retire "${asset.name}"? It will no longer appear in scheduling or reports.`,
                            )
                        ) {
                            retire.mutate();
                        }
                    }}
                    disabled={retire.isPending}
                >
                    Retire
                </Button>
            )}
        </div>
    );
}

function DueChipTile({
    label,
    date,
    icon,
    neutral,
}: {
    label: string;
    date: string | null;
    icon: React.ReactNode;
    neutral?: boolean;
}) {
    const now = Date.now();
    const ms = date ? new Date(date).getTime() : null;
    const overdue = !!ms && !neutral && ms < now;
    const soon = !!ms && !neutral && !overdue && ms - now < 90 * 24 * 60 * 60 * 1000;

    return (
        <StatTile label={label} icon={icon} tone={overdue ? "danger" : soon ? "warn" : "neutral"} className="px-3">
            <div className="mt-0.5 text-sm font-medium tabular-nums">
                {date ? formatDate(date) : <span className="italic opacity-70">Not set</span>}
            </div>
        </StatTile>
    );
}

// Editable variant of the next-due tile. Office staff need a path to
// correct due dates that drifted in from the ServiceTitan import or that
// were never set, since a NULL due date is an invisible compliance gap.
function EditableDueChipTile({
    assetId,
    date,
    proof,
}: {
    assetId: string;
    date: string | null;
    /** How sure the date is (proof chips, switch on); null = no chip. */
    proof?: Proof | null;
}) {
    const qc = useQueryClient();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(date ?? "");

    const now = Date.now();
    const ms = date ? new Date(date).getTime() : null;
    const overdue = !!ms && ms < now;
    const soon = !!ms && !overdue && ms - now < 90 * 24 * 60 * 60 * 1000;
    const missing = !date;

    const save = useMutation({
        mutationFn: (value: string | null) => api.patch(`/api/assets/${assetId}`, { nextDueDate: value }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["asset", assetId] });
            toast.success("Due date updated");
            setEditing(false);
        },
        onError: () => toast.error("Could not update due date"),
    });

    return (
        // `missing` takes the warn tone deliberately: a device with NO due date
        // is invisible in every due queue, which is worse than being overdue,
        // not better. It is also the most common state on an imported device.
        <StatTile
            label="Next due"
            icon={<Calendar className="h-3 w-3" />}
            tone={overdue ? "danger" : soon || missing ? "warn" : "neutral"}
            className="px-3"
            action={
                !editing ? (
                    <button
                        type="button"
                        className="normal-case tracking-normal text-primary hover:underline"
                        onClick={() => {
                            setDraft(date ?? "");
                            setEditing(true);
                        }}
                    >
                        Edit
                    </button>
                ) : null
            }
        >
            {editing ? (
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <input
                        type="date"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                    />
                    <Button
                        size="sm"
                        className="h-8"
                        disabled={save.isPending}
                        onClick={() => save.mutate(draft || null)}
                    >
                        Save
                    </Button>
                    {date && (
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            disabled={save.isPending}
                            onClick={() => save.mutate(null)}
                        >
                            Clear
                        </Button>
                    )}
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-8"
                        disabled={save.isPending}
                        onClick={() => setEditing(false)}
                    >
                        Cancel
                    </Button>
                </div>
            ) : (
                <div className="mt-0.5 text-sm font-medium tabular-nums">
                    {date ? (
                        // A DATE column: formatDateOnly, or Central shows the day before.
                        formatDateOnly(date)
                    ) : (
                        // `text-warning` on the tile's opaque card surface —
                        // NOT `text-warning-foreground`, which is the colour
                        // that sits ON a solid warning fill and is invisible
                        // as bare text in both themes.
                        <span className="inline-flex items-center gap-1 text-warning">
                            <AlertCircle className="h-3.5 w-3.5" /> No due date
                        </span>
                    )}
                </div>
            )}
            {!editing && proof && (
                <div className="mt-1.5">
                    <ProofChip
                        proof={proof}
                        place="page"
                        testId="due-proof-chip"
                        onFix={(fix) => {
                            if (fix.action === "edit_due_date") {
                                setDraft(date ?? "");
                                setEditing(true);
                            }
                        }}
                    />
                </div>
            )}
        </StatTile>
    );
}

function formatServes(v: string): string {
    return v.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

// ── Tabs ─────────────────────────────────────────────────────────────

function AssetTabs({ assetId, asset, proof }: { assetId: string; asset: AssetDetail; proof?: DeviceProof | null }) {
    const [tab, setTab] = useState<"overview" | "tests" | "history" | "attachments">("overview");

    return (
        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
            <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="tests">Tests & Inspections</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
                <TabsTrigger value="attachments">Attachments</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="mt-4 space-y-4">
                <OverviewTab asset={asset} />
                <CrossConnectionPanel asset={asset} />
            </TabsContent>
            <TabsContent value="tests" className="mt-4">
                <TestsTab
                    assetId={assetId}
                    proofs={proof?.tests ?? null}
                    assetLabel={asset.serial_number ? `${asset.name} · SN ${asset.serial_number}` : asset.name}
                />
            </TabsContent>
            <TabsContent value="history" className="mt-4">
                <HistoryTab assetId={assetId} />
            </TabsContent>
            <TabsContent value="attachments" className="mt-4">
                <AttachmentsTab assetId={assetId} />
            </TabsContent>
        </Tabs>
    );
}

// ── Overview ─────────────────────────────────────────────────────────

function OverviewTab({ asset }: { asset: AssetDetail }) {
    // Issue #1024: every spec here used to be read-only text, so an import
    // artifact (this device shipped in with LOCATION ON PROPERTY =
    // "Information Contact Information") could not be corrected anywhere in
    // the app — even though PATCH /api/assets/:id has always accepted the
    // whole set. Edit flips the card into a form over the same fields.
    const [editing, setEditing] = useState(false);
    if (editing) return <SpecsEditor asset={asset} onDone={() => setEditing(false)} />;
    return <SpecsCard asset={asset} onEdit={() => setEditing(true)} />;
}

function SpecsCard({ asset, onEdit }: { asset: AssetDetail; onEdit: () => void }) {
    const rows: Array<{ label: string; value: React.ReactNode }> = [
        { label: "Manufacturer", value: asset.manufacturer ?? "—" },
        { label: "Model", value: asset.model ?? "—" },
        { label: "Size", value: asset.size_label ?? "—" },
        {
            label: "Assembly",
            value: asset.assembly_type
                ? (ASSEMBLY_LABEL[asset.assembly_type] ?? asset.assembly_type.toUpperCase())
                : "—",
        },
        { label: "Serial #", value: asset.serial_number ?? "—" },
        { label: "Meter #", value: asset.meter_number ?? "—" },
        {
            label: "Serves",
            value: asset.service_type ? formatServes(asset.service_type) : (asset.service_type_other ?? "—"),
        },
        {
            label: "Fireline",
            value: asset.is_fireline ? "Yes" : "No",
        },
        {
            label: "Detector assembly",
            value: asset.is_detector_assembly ? "Yes" : "No",
        },
        {
            label: "In vault",
            value: asset.in_vault === null ? "—" : asset.in_vault ? "Yes" : "No",
        },
        {
            label: "Installed",
            value: INSTALL_SETTING_OPTIONS.find((o) => o.value === asset.install_setting)?.label ?? "Not known",
        },
        { label: "Location on property", value: asset.location_description ?? "—" },
        {
            // #1011: this row used to read "Created <timestamp>" on every
            // device, including one whose whole history is a ServiceTitan
            // import. Next to Manufacturer, Model and Serial #, a date
            // labelled "Created" reads as an INSTALL date — and on an
            // imported row it is nothing of the kind. It is the minute a
            // sync process wrote the record, often years after the assembly
            // went into the ground.
            //
            // So the label follows the provenance: an imported device says
            // so, in words, rather than presenting a sync timestamp as a
            // fact about the hardware.
            label: asset.external_key ? "Imported" : "Added to Groundwork",
            value: (
                <span>
                    {formatDateTime(asset.created_at)}
                    {asset.external_key && (
                        <span className="ml-1.5 block text-xs font-normal text-foreground/70 sm:inline">
                            imported — not an install date
                        </span>
                    )}
                </span>
            ),
        },
        {
            label: "Updated",
            value: formatDateTime(asset.updated_at),
        },
    ];

    return (
        <Card>
            <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">Specs</CardTitle>
                <Button size="sm" variant="outline" onClick={onEdit}>
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
            </CardHeader>
            <CardContent>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {rows.map((r) => (
                        <div
                            key={r.label}
                            className="flex justify-between items-baseline border-b border-border/40 pb-2 last:border-0"
                        >
                            <dt className="text-xs uppercase tracking-wide text-foreground/80">{r.label}</dt>
                            <dd className="text-sm font-medium text-right">{r.value}</dd>
                        </div>
                    ))}
                </dl>
                {asset.notes && (
                    <div className="mt-4 rounded-lg bg-muted/50 border px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wide text-foreground/80">Notes</div>
                        <div className="text-sm whitespace-pre-wrap mt-0.5">{asset.notes}</div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// ── Specs editor ─────────────────────────────────────────────────────
//
// The correction surface for a device's identity (Issue #1024). Deliberately
// plain fields rather than the tech workflow's USC size→make→model cascade:
// this page serves every asset kind, and someone fixing ONE wrong value must
// not have the other four reset underneath them because the assembly type
// changed. `buildAssetSpecsPatch` sends only what actually moved, so an
// untouched legacy enum value that the PATCH route no longer accepts survives
// an unrelated edit instead of failing validation.

/** Add the row's current value to an enum <select> when it predates the list. */
function withCurrentValue(
    options: ReadonlyArray<{ value: string; label: string }>,
    current: string,
): Array<{ value: string; label: string }> {
    if (!current || options.some((o) => o.value === current)) return [...options];
    return [...options, { value: current, label: `${current.replace(/_/g, " ")} (legacy)` }];
}

function SpecsEditor({ asset, onDone }: { asset: AssetDetail; onDone: () => void }) {
    const qc = useQueryClient();
    const [form, setForm] = useState<AssetSpecsFormValues>(() => assetSpecsFormValues(asset));
    const set = (patch: Partial<AssetSpecsFormValues>) => setForm((f) => ({ ...f, ...patch }));

    const save = useMutation({
        mutationFn: () => {
            const body = buildAssetSpecsPatch(asset, form);
            if (isEmptyPatch(body)) return Promise.resolve(null);
            return api.patch(`/api/assets/${asset.id}`, body);
        },
        onSuccess: (result) => {
            // NO SILENT SUCCESS: say which of the two things happened.
            if (result === null) {
                toast.info("Nothing changed");
            } else {
                toast.success("Device updated");
            }
            void qc.invalidateQueries({ queryKey: ["asset", asset.id] });
            void qc.invalidateQueries({ queryKey: ["site-assets", asset.site_id] });
            void qc.invalidateQueries({ queryKey: ["asset-activity", asset.id] });
            onDone();
        },
        onError: (e: unknown) => {
            const msg = e instanceof ApiError ? ((e.body as { message?: string })?.message ?? e.message) : String(e);
            toast.error(`Device NOT saved: ${msg}`);
        },
    });

    const nameBlank = form.name.trim() === "";

    const assemblyOptions = withCurrentValue(ASSEMBLY_TYPES, form.assemblyType);
    const servesOptions = withCurrentValue(SERVICE_TYPES, form.serviceType);

    return (
        <Card>
            <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">Edit specs</CardTitle>
                <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={onDone} disabled={save.isPending}>
                        Cancel
                    </Button>
                    <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || nameBlank}>
                        {save.isPending ? "Saving…" : "Save"}
                    </Button>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                <div>
                    <Label htmlFor="spec-name">Device name</Label>
                    <Input
                        id="spec-name"
                        value={form.name}
                        onChange={(e) => set({ name: e.target.value })}
                        aria-invalid={nameBlank}
                    />
                    <p className="text-xs text-foreground/60 mt-1">
                        {nameBlank
                            ? "A device needs a name."
                            : "What this device is called on lists, jobs and reports."}
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                    <div>
                        <Label htmlFor="spec-assembly">Assembly</Label>
                        <Select
                            id="spec-assembly"
                            value={form.assemblyType}
                            onChange={(e) => set({ assemblyType: e.target.value })}
                        >
                            <option value="">—</option>
                            {assemblyOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="spec-size">Size</Label>
                        <Input
                            id="spec-size"
                            value={form.sizeLabel}
                            onChange={(e) => set({ sizeLabel: e.target.value })}
                            placeholder='e.g. 1"'
                        />
                    </div>
                    <div>
                        <Label htmlFor="spec-manufacturer">Manufacturer</Label>
                        <Input
                            id="spec-manufacturer"
                            value={form.manufacturer}
                            onChange={(e) => set({ manufacturer: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label htmlFor="spec-model">Model</Label>
                        {/* Device check (11/2): the model is a tap list from the state list. */}
                        <ModelTapList
                            id="spec-model"
                            value={form.model}
                            onChange={(v) => set({ model: v })}
                            manufacturer={form.manufacturer}
                            assemblyType={form.assemblyType}
                            sizeLabel={form.sizeLabel}
                        />
                    </div>
                    <div>
                        <Label htmlFor="spec-serial">Serial #</Label>
                        <Input
                            id="spec-serial"
                            value={form.serialNumber}
                            onChange={(e) => set({ serialNumber: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label htmlFor="spec-meter">Meter #</Label>
                        <Input
                            id="spec-meter"
                            value={form.meterNumber}
                            onChange={(e) => set({ meterNumber: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label htmlFor="spec-serves">Serves</Label>
                        <Select
                            id="spec-serves"
                            value={form.serviceType}
                            onChange={(e) => set({ serviceType: e.target.value })}
                        >
                            <option value="">—</option>
                            {servesOptions.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                    </div>
                    {form.serviceType === "other" && (
                        <div>
                            <Label htmlFor="spec-serves-other">Serves — specify</Label>
                            <Input
                                id="spec-serves-other"
                                value={form.serviceTypeOther}
                                onChange={(e) => set({ serviceTypeOther: e.target.value })}
                            />
                        </div>
                    )}
                    <div>
                        <Label htmlFor="spec-vault">In vault</Label>
                        <Select id="spec-vault" value={form.inVault} onChange={(e) => set({ inVault: e.target.value })}>
                            <option value="">Unknown</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                        </Select>
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <Label htmlFor="spec-installed">Installed</Label>
                            <InfoButton topic={INSTALLED_HELP} />
                        </div>
                        <Select
                            id="spec-installed"
                            value={form.installSetting}
                            onChange={(e) => set({ installSetting: e.target.value })}
                        >
                            <option value="">Not known</option>
                            {INSTALL_SETTING_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </Select>
                    </div>
                    <div className="flex items-end gap-6 pb-1">
                        <label className="inline-flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={form.isFireline}
                                onChange={(e) => set({ isFireline: e.target.checked })}
                            />
                            Fireline
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={form.isDetectorAssembly}
                                onChange={(e) => set({ isDetectorAssembly: e.target.checked })}
                            />
                            Detector assembly
                        </label>
                    </div>
                </div>

                <div>
                    <Label htmlFor="spec-location">Location on property</Label>
                    <Input
                        id="spec-location"
                        value={form.locationDescription}
                        onChange={(e) => set({ locationDescription: e.target.value })}
                        placeholder="e.g. NE corner, behind the dumpster enclosure"
                    />
                    <p className="text-xs text-foreground/60 mt-1">
                        Where the tech will find it on site — not a contact or an address.
                    </p>
                </div>

                <div>
                    <Label htmlFor="spec-notes">Notes</Label>
                    <Textarea
                        id="spec-notes"
                        rows={3}
                        value={form.notes}
                        onChange={(e) => set({ notes: e.target.value })}
                    />
                </div>
            </CardContent>
        </Card>
    );
}

// ── Cross-connection details (template-driven gate inputs) ──────────
//
// Surfaces the four fields jurisdictions ask for that go beyond the
// TCEQ baseline:
//   • Hazard classification (Health vs Non-Health)
//   • Description of hazard (free text)
//   • Protected service (Irrigation, CO2/soda, Point of service, Fire
//     sprinkler, Other)
//   • Other (specify) when the category is "other"
//
// Visibility rule: the panel is shown for every backflow asset so
// techs in non-gated jurisdictions can still capture the data when
// they want to, but the "Required" badge + red border only fires
// when the asset's water-provider form template marks
// `asset.hazard_type` (or related paths) as `is_required`. That way
// we never surprise a Garland tech with a blocking field they don't
// need, but Midlothian-style jurisdictions can't sneak past the gate.

const CATEGORY_LABEL: Record<NonNullable<AssetDetail["protected_service_category"]>, string> = {
    irrigation: "Irrigation",
    co2_soda: "CO2 / Soda fountain",
    fire_sprinkler: "Fire sprinkler",
    point_of_service: "Point of service",
    other: "Other",
};

function CrossConnectionPanel({ asset }: { asset: AssetDetail }) {
    const qc = useQueryClient();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({
        hazard_classification: asset.hazard_classification,
        hazard_description: asset.hazard_description ?? "",
        protected_service_category: asset.protected_service_category,
        protected_service_other: asset.protected_service_other ?? "",
    });

    // Template-driven gating: the panel goes "Required" when the
    // active jurisdiction's form template marks any cross-connection
    // path as `is_required`. Falls back to "not required" while the
    // endpoint is loading.
    const required = useRequiredAssetFields(asset.site_id, "backflow");
    const gatedPaths = new Set((required.data?.requiredColumns ?? []).map((c) => c.path));
    const isGated =
        gatedPaths.has("asset.hazard_type") ||
        gatedPaths.has("asset.protected_service_category") ||
        gatedPaths.has("asset.hazard_description") ||
        gatedPaths.has("asset.protected_service_other");
    const isBackflow = (asset.asset_kind ?? "").toLowerCase() === "backflow";

    const save = useMutation({
        mutationFn: () =>
            api.patch(`/api/assets/${asset.id}`, {
                hazardClassification: draft.hazard_classification,
                hazardDescription: draft.hazard_description.trim() || null,
                protectedServiceCategory: draft.protected_service_category,
                protectedServiceOther:
                    draft.protected_service_category === "other" ? draft.protected_service_other.trim() || null : null,
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["asset", asset.id] });
            toast.success("Cross-connection details saved");
            setEditing(false);
        },
        onError: () => toast.error("Couldn't save cross-connection details"),
    });

    // The "not a backflow, nothing to show" exit sits BELOW every hook on
    // purpose: a hook that only runs on some renders is what React counts as
    // a changed hook order, and the page dies on minified error #310.
    if (!isBackflow) return null;

    const missingClassification = !asset.hazard_classification;
    const missingCategory = !asset.protected_service_category;
    const missingOther = asset.protected_service_category === "other" && !asset.protected_service_other;
    const blocking = isGated && (missingClassification || missingCategory || missingOther);

    return (
        <Card className={cn(blocking && "border-destructive/50")}>
            <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <CardTitle className="text-base">Cross-connection details</CardTitle>
                    {isGated && (
                        <Badge
                            variant="outline"
                            className="border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                        >
                            Required for this jurisdiction
                        </Badge>
                    )}
                    {blocking && (
                        <Badge variant="destructive" className="gap-1">
                            <AlertCircle className="h-3 w-3" /> Missing
                        </Badge>
                    )}
                </div>
                {!editing && (
                    <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                        Edit
                    </Button>
                )}
            </CardHeader>
            <CardContent>
                {editing ? (
                    <CrossConnectionEditor
                        draft={draft}
                        setDraft={setDraft}
                        onCancel={() => {
                            setDraft({
                                hazard_classification: asset.hazard_classification,
                                hazard_description: asset.hazard_description ?? "",
                                protected_service_category: asset.protected_service_category,
                                protected_service_other: asset.protected_service_other ?? "",
                            });
                            setEditing(false);
                        }}
                        onSave={() => save.mutate()}
                        saving={save.isPending}
                    />
                ) : (
                    <CrossConnectionReadOnly asset={asset} />
                )}
            </CardContent>
        </Card>
    );
}

function CrossConnectionReadOnly({ asset }: { asset: AssetDetail }) {
    const rows: Array<{ label: string; value: React.ReactNode }> = [
        {
            label: "Hazard classification",
            value: asset.hazard_classification
                ? asset.hazard_classification === "health"
                    ? "Health"
                    : "Non-Health"
                : "—",
        },
        {
            label: "Description of hazard",
            value: asset.hazard_description?.trim() || "—",
        },
        {
            label: "Protected service",
            value: asset.protected_service_category ? CATEGORY_LABEL[asset.protected_service_category] : "—",
        },
        {
            label: "Other (specify)",
            value: asset.protected_service_category === "other" ? asset.protected_service_other?.trim() || "—" : "—",
        },
    ];
    return (
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {rows.map((r) => (
                <div
                    key={r.label}
                    className="flex justify-between items-baseline border-b border-border/40 pb-2 last:border-0"
                >
                    <dt className="text-xs uppercase tracking-wide text-foreground/80">{r.label}</dt>
                    <dd className="text-sm font-medium text-right">{r.value}</dd>
                </div>
            ))}
        </dl>
    );
}

interface CrossConnectionDraft {
    hazard_classification: AssetDetail["hazard_classification"];
    hazard_description: string;
    protected_service_category: AssetDetail["protected_service_category"];
    protected_service_other: string;
}

function CrossConnectionEditor({
    draft,
    setDraft,
    onCancel,
    onSave,
    saving,
}: {
    draft: CrossConnectionDraft;
    setDraft: (next: CrossConnectionDraft) => void;
    onCancel: () => void;
    onSave: () => void;
    saving: boolean;
}) {
    return (
        <div className="space-y-4">
            <div>
                <div className="text-xs uppercase tracking-wide text-foreground/80 mb-1.5">Hazard classification</div>
                <div className="flex gap-2 flex-wrap">
                    {(["health", "non_health"] as const).map((opt) => (
                        <button
                            key={opt}
                            type="button"
                            onClick={() => setDraft({ ...draft, hazard_classification: opt })}
                            className={cn(
                                "px-3 py-1.5 rounded-md border text-sm",
                                draft.hazard_classification === opt
                                    ? "border-primary bg-primary/10 text-foreground"
                                    : "border-border text-foreground/80 hover:bg-muted",
                            )}
                        >
                            {opt === "health" ? "Health" : "Non-Health"}
                        </button>
                    ))}
                    {draft.hazard_classification && (
                        <button
                            type="button"
                            onClick={() => setDraft({ ...draft, hazard_classification: null })}
                            className="px-3 py-1.5 rounded-md border border-border text-xs text-foreground/70 hover:bg-muted"
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>
            <div>
                <div className="text-xs uppercase tracking-wide text-foreground/80 mb-1.5">Description of hazard</div>
                <textarea
                    rows={2}
                    maxLength={500}
                    value={draft.hazard_description}
                    onChange={(e) => setDraft({ ...draft, hazard_description: e.target.value })}
                    placeholder="e.g. Cooling tower glycol, Irrigation backflow into yard"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
            </div>
            <div>
                <div className="text-xs uppercase tracking-wide text-foreground/80 mb-1.5">Protected service</div>
                <div className="flex gap-2 flex-wrap">
                    {(["irrigation", "co2_soda", "fire_sprinkler", "point_of_service", "other"] as const).map((opt) => (
                        <button
                            key={opt}
                            type="button"
                            onClick={() => setDraft({ ...draft, protected_service_category: opt })}
                            className={cn(
                                "px-3 py-1.5 rounded-md border text-sm",
                                draft.protected_service_category === opt
                                    ? "border-primary bg-primary/10 text-foreground"
                                    : "border-border text-foreground/80 hover:bg-muted",
                            )}
                        >
                            {CATEGORY_LABEL[opt]}
                        </button>
                    ))}
                    {draft.protected_service_category && (
                        <button
                            type="button"
                            onClick={() =>
                                setDraft({
                                    ...draft,
                                    protected_service_category: null,
                                    protected_service_other: "",
                                })
                            }
                            className="px-3 py-1.5 rounded-md border border-border text-xs text-foreground/70 hover:bg-muted"
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>
            {draft.protected_service_category === "other" && (
                <div>
                    <div className="text-xs uppercase tracking-wide text-foreground/80 mb-1.5">
                        Other — please specify
                    </div>
                    <input
                        type="text"
                        maxLength={255}
                        value={draft.protected_service_other}
                        onChange={(e) => setDraft({ ...draft, protected_service_other: e.target.value })}
                        placeholder="e.g. Photographic developer line"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    />
                </div>
            )}
            <div className="flex gap-2 justify-end pt-1">
                <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
                    Cancel
                </Button>
                <Button size="sm" onClick={onSave} disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                </Button>
            </div>
        </div>
    );
}

// ── Tests tab ────────────────────────────────────────────────────────

function TestsTab({
    assetId,
    proofs,
    assetLabel,
}: {
    assetId: string;
    /** Proof chips by test id (switch on); null = the page as before. */
    proofs?: Record<string, Proof> | null;
    assetLabel?: string;
}) {
    const [expanded, setExpanded] = useState(false);
    const q = useQuery({
        queryKey: ["asset-activity", assetId],
        queryFn: () => api.get<ActivityResponse>(`/api/assets/${assetId}/activity?limit=200`),
        enabled: !!assetId,
    });

    const tests = q.data?.tests ?? [];

    const cols: Column<ActivityResponse["tests"][number]>[] = [
        {
            key: "performed_at",
            header: "Date",
            sortable: true,
            firstSortDir: "desc",
            sortAccessor: (r) => dateSortValue(r.performed_at),
            render: (r) => (
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span>{formatDate(r.performed_at)}</span>
                    {proofs ? (
                        proofs[r.id] ? (
                            <TestProofChip
                                proof={proofs[r.id]}
                                assetId={assetId}
                                assetLabel={assetLabel ?? "Device"}
                                jobId={r.job_id}
                            />
                        ) : null
                    ) : null}
                    {!proofs && r.source === "st_backfill" && (
                        <Badge
                            variant="outline"
                            className="text-[9px] uppercase border-amber-500/40 text-amber-700 bg-amber-500/10"
                            title="Reconstructed from an imported internal test form. Refer to the original report for compliance."
                        >
                            ST
                        </Badge>
                    )}
                    {!proofs && r.source === "vepo" && (
                        <Badge
                            variant="outline"
                            className="text-[9px] uppercase border-sky-500/40 text-sky-700 bg-sky-500/10"
                            title="Imported from a VEPO city-portal record."
                        >
                            VEPO
                        </Badge>
                    )}
                </div>
            ),
        },
        {
            key: "kind",
            header: "Type",
            sortable: true,
            render: (r) => (
                <Badge variant="outline" className="text-[10px] uppercase">
                    {r.kind === "backflow" ? "Backflow" : "Fire safety"}
                </Badge>
            ),
        },
        {
            key: "overall_result",
            header: "Result",
            sortable: true,
            sortAccessor: (r) => (r.overall_result ? r.overall_result.toLowerCase() : null),
            render: (r) => {
                const v = (r.overall_result ?? "").toLowerCase();
                return v ? (
                    <Badge
                        variant="outline"
                        className={cn(
                            "uppercase text-[10px]",
                            v === "pass" && "border-emerald-500/40 text-emerald-600 bg-emerald-500/10",
                            v === "fail" && "border-destructive/40 text-destructive bg-destructive/10",
                        )}
                    >
                        {testStateLabel(v)}
                    </Badge>
                ) : (
                    <span className="text-foreground/80">—</span>
                );
            },
        },
        {
            key: "job_id",
            header: "Job",
            sortable: true,
            sortAccessor: (r) => (r.job_id ? (r.job_number ?? r.job_id.slice(0, 8)) : null),
            render: (r) =>
                r.job_id ? (
                    <Link to={`/jobs/${r.job_id}`} className="font-mono text-xs text-primary hover:underline">
                        #{r.job_number ?? r.job_id.slice(0, 8)}
                    </Link>
                ) : (
                    <span className="text-foreground/80">—</span>
                ),
        },
    ];
    // Sort every test before the 10-row preview cuts them down.
    const { sorted: sortedTests, controlledSort: testsSort } = useSortedRows(tests, cols, {
        sortId: "assets.detail.tests",
    });
    const visibleTests = expanded ? sortedTests : sortedTests.slice(0, DEFAULT_VISIBLE_ROWS);

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4" /> Tests & Inspections ({tests.length})
                </CardTitle>
            </CardHeader>
            <CardContent>
                <DataTable
                    columns={cols}
                    data={visibleTests}
                    keyField="id"
                    controlledSort={testsSort}
                    loading={q.isLoading}
                    emptyMessage="No tests on file yet. They appear here automatically once a test job is completed."
                />
                <ShowMoreFooter total={tests.length} expanded={expanded} onToggle={() => setExpanded((v) => !v)} />
            </CardContent>
        </Card>
    );
}

// ── History (device test/repair timeline) ────────────────────────────
//
// Focused on the device's compliance story: each test shows Date -> result,
// and a failed test nests its repair(s) and the verifying retest result. Job
// lifecycle / photo / generic-audit noise is intentionally left out (it lives
// on the job timeline); only device-level status changes are kept.

type RepairLine = { id: string; at: string; label: string };
type RetestLine = { id: string; at: string; result: string | null; jobId: string | null };

type TimelineItem =
    | {
          kind: "status";
          id: string;
          at: string;
          from: string | null;
          to: string;
          reason: string | null;
          who: string | null;
      }
    | {
          kind: "test";
          id: string;
          at: string;
          result: string | null;
          jobId: string | null;
          type: "backflow" | "fire_safety";
          source?: "native" | "st_backfill" | "historical_pdf" | "vepo";
          repairs: RepairLine[];
          retest: RetestLine | null;
      };

/** Humanize a structured repair tag (e.g. "replace_cv1_rubber"). */
function humanizeTag(s: string): string {
    return s.replace(/[._-]+/g, " ").trim();
}

/** Pull a short list of repair tags out of the JSONB repairs_confirmed value. */
function repairTags(raw: unknown): string[] {
    if (!Array.isArray(raw)) return [];
    const out: string[] = [];
    for (const v of raw) {
        if (typeof v === "string") {
            const t = humanizeTag(v);
            if (t) out.push(t);
        } else if (v && typeof v === "object") {
            const o = v as Record<string, unknown>;
            const label = o.label ?? o.name ?? o.tag ?? o.repair ?? o.id;
            if (typeof label === "string") {
                const t = humanizeTag(label);
                if (t) out.push(t);
            }
        }
    }
    return out;
}

/** Human-readable description of what the repair did. */
function repairLabel(r: { repair_notes: string | null; repairs_confirmed?: unknown }): string {
    const note = (r.repair_notes ?? "").trim();
    if (note) return note;
    const tags = repairTags(r.repairs_confirmed);
    if (tags.length) return tags.join(", ");
    return "Repair recorded";
}

function ResultBadge({ value }: { value: string }) {
    return (
        <Badge
            variant="outline"
            className={cn(
                "uppercase text-[10px]",
                value === "pass" && "border-emerald-500/40 text-emerald-600 bg-emerald-500/10",
                value === "fail" && "border-destructive/40 text-destructive bg-destructive/10",
            )}
        >
            {value}
        </Badge>
    );
}

function HistoryTab({ assetId }: { assetId: string }) {
    const [expanded, setExpanded] = useState(false);
    const q = useQuery({
        queryKey: ["asset-activity", assetId],
        queryFn: () => api.get<ActivityResponse>(`/api/assets/${assetId}/activity?limit=200`),
        enabled: !!assetId,
    });

    const items = useMemo<TimelineItem[]>(() => {
        const tests = q.data?.tests ?? [];
        const repairs = q.data?.repairs ?? [];
        const testsById = new Map(tests.map((t) => [t.id, t]));

        // Retests: a test whose original_test_id resolves to another test in
        // this set is the verifying retest of that failing test. Fold it into
        // the parent and keep it off the top level.
        const retestByOriginal = new Map<string, RetestLine>();
        const consumed = new Set<string>();
        for (const t of tests) {
            const orig = t.original_test_id ?? null;
            if (orig && testsById.has(orig)) {
                consumed.add(t.id);
                const existing = retestByOriginal.get(orig);
                if (!existing || +new Date(t.performed_at) > +new Date(existing.at)) {
                    retestByOriginal.set(orig, {
                        id: t.id,
                        at: t.performed_at,
                        result: t.overall_result,
                        jobId: t.job_id,
                    });
                }
            }
        }

        // Pair each repair to the failing test it closes out.
        const failTests = tests.filter((t) => (t.overall_result ?? "").toLowerCase() === "fail");
        const repairsByTest = new Map<string, RepairLine[]>();
        const pushRepair = (testId: string, r: (typeof repairs)[number]) => {
            const arr = repairsByTest.get(testId) ?? [];
            arr.push({ id: r.id, at: r.performed_at, label: repairLabel(r) });
            repairsByTest.set(testId, arr);
        };
        for (const r of repairs) {
            // 1. explicit link to the failing test.
            if (r.original_test_id && testsById.has(r.original_test_id)) {
                pushRepair(r.original_test_id, r);
                continue;
            }
            // 2. same job — prefer a failing test on that job.
            const sameJob = r.job_id ? tests.filter((t) => t.job_id && t.job_id === r.job_id) : [];
            const jobMatch = sameJob.find((t) => (t.overall_result ?? "").toLowerCase() === "fail") ?? sameJob[0];
            if (jobMatch) {
                pushRepair(jobMatch.id, r);
                continue;
            }
            // 3. nearest failing test at/just before the repair date (tests are desc).
            const preceding =
                failTests.find((t) => +new Date(t.performed_at) <= +new Date(r.performed_at)) ?? failTests[0];
            if (preceding) pushRepair(preceding.id, r);
        }

        const out: TimelineItem[] = [];
        for (const t of tests) {
            if (consumed.has(t.id)) continue;
            out.push({
                kind: "test",
                id: t.id,
                at: t.performed_at,
                result: t.overall_result,
                jobId: t.job_id,
                type: t.kind,
                source: t.source,
                repairs: repairsByTest.get(t.id) ?? [],
                retest: retestByOriginal.get(t.id) ?? null,
            });
        }
        for (const s of q.data?.status_history ?? []) {
            out.push({
                kind: "status",
                id: s.id,
                at: s.created_at,
                from: s.from_status,
                to: s.to_status,
                reason: s.reason,
                who: s.changed_by_name,
            });
        }
        return out.sort((a, b) => +new Date(b.at) - +new Date(a.at));
    }, [q.data]);

    if (q.isLoading) {
        return (
            <Card>
                <CardContent className="py-6">
                    <Skeleton className="h-32 w-full" />
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <History className="h-4 w-4" /> Timeline ({items.length})
                </CardTitle>
            </CardHeader>
            <CardContent>
                {items.length === 0 ? (
                    <p className="text-sm text-foreground/80 py-8 text-center">
                        Nothing's happened to this device yet.
                    </p>
                ) : (
                    <>
                        <ol className="relative border-l border-border ml-2 space-y-4 pl-5">
                            {(expanded ? items : items.slice(0, DEFAULT_VISIBLE_ROWS)).map((it) => (
                                <li key={it.id} className="relative">
                                    <span
                                        className={cn(
                                            "absolute -left-[27px] top-1 h-3 w-3 rounded-full border-2 bg-background",
                                            it.kind === "test" &&
                                                ((it.result ?? "").toLowerCase() === "fail"
                                                    ? "border-destructive"
                                                    : "border-emerald-500"),
                                            it.kind === "status" && "border-amber-500",
                                        )}
                                    />
                                    <TimelineRow item={it} />
                                </li>
                            ))}
                        </ol>
                        <ShowMoreFooter
                            total={items.length}
                            expanded={expanded}
                            onToggle={() => setExpanded((v) => !v)}
                        />
                    </>
                )}
            </CardContent>
        </Card>
    );
}

function TimelineRow({ item }: { item: TimelineItem }) {
    if (item.kind === "status") {
        return (
            <div>
                <div className="flex items-center gap-2 flex-wrap">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-600" />
                    <span className="text-sm">
                        Status changed{" "}
                        {item.from && (
                            <>
                                from <Badge variant="outline">{item.from}</Badge>{" "}
                            </>
                        )}
                        to <Badge variant="outline">{item.to}</Badge>
                    </span>
                    {item.who && <span className="text-xs text-foreground/80">by {item.who}</span>}
                </div>
                {item.reason && <div className="text-xs text-foreground/80 mt-0.5 italic">"{item.reason}"</div>}
                <div className="text-[11px] text-foreground/80 tabular-nums">{formatDateTime(item.at)}</div>
            </div>
        );
    }

    const result = (item.result ?? "").toLowerCase();
    const hasFollowups = item.repairs.length > 0 || !!item.retest;
    return (
        <div>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground/80 tabular-nums">{formatDate(item.at)}</span>
                <ClipboardCheck className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-sm font-medium">
                    {item.type === "backflow" ? "Backflow test" : "Fire safety inspection"}
                </span>
                {result && <ResultBadge value={result} />}
                {item.source === "vepo" && (
                    <Badge
                        variant="outline"
                        className="text-[9px] uppercase border-sky-500/40 text-sky-700 bg-sky-500/10"
                        title="Imported from a VEPO city-portal record."
                    >
                        VEPO
                    </Badge>
                )}
                {item.source === "st_backfill" && (
                    <Badge
                        variant="outline"
                        className="text-[9px] uppercase border-amber-500/40 text-amber-700 bg-amber-500/10"
                        title="Reconstructed from an imported internal test form. Refer to the original report for compliance."
                    >
                        ST
                    </Badge>
                )}
                {item.jobId && (
                    <Link to={`/jobs/${item.jobId}`} className="text-xs text-primary hover:underline">
                        view job
                    </Link>
                )}
            </div>
            {hasFollowups && (
                <ol className="mt-1.5 ml-2 border-l border-border/60 space-y-1.5 pl-3">
                    {item.repairs.map((rep) => (
                        <li key={rep.id} className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-foreground/80 tabular-nums">{formatDate(rep.at)}</span>
                            <Wrench className="h-3.5 w-3.5 text-amber-600" />
                            <span className="text-sm">Repair</span>
                            <span className="text-xs text-foreground/80">{rep.label}</span>
                        </li>
                    ))}
                    {item.retest && (
                        <li className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-foreground/80 tabular-nums">
                                {formatDate(item.retest.at)}
                            </span>
                            <ClipboardCheck className="h-3.5 w-3.5 text-emerald-600" />
                            <span className="text-sm">Retest</span>
                            {item.retest.result && <ResultBadge value={(item.retest.result ?? "").toLowerCase()} />}
                            {item.retest.jobId && (
                                <Link
                                    to={`/jobs/${item.retest.jobId}`}
                                    className="text-xs text-primary hover:underline"
                                >
                                    view job
                                </Link>
                            )}
                        </li>
                    )}
                </ol>
            )}
        </div>
    );
}

// ── Attachments ──────────────────────────────────────────────────────

function AttachmentsTab({ assetId }: { assetId: string }) {
    const [docsExpanded, setDocsExpanded] = useState(false);
    const [photosExpanded, setPhotosExpanded] = useState(false);
    const [previewAttachment, setPreviewAttachment] = useState<AttachmentRow | null>(null);

    const q = useQuery({
        queryKey: ["asset-attachments", assetId],
        queryFn: () =>
            api.get<{ attachments: AttachmentRow[]; total: number }>(`/api/assets/${assetId}/attachments?limit=200`),
        enabled: !!assetId,
        staleTime: 60_000,
    });

    const rows = q.data?.attachments ?? [];
    const total = q.data?.total ?? 0;

    const photos = rows.filter((r) => (r.content_type ?? "").startsWith("image/"));
    const docs = rows.filter((r) => !(r.content_type ?? "").startsWith("image/"));
    const docColumns: Column<AttachmentRow>[] = [
        {
            key: "file_name",
            header: "File",
            sortable: true,
            render: (r) => {
                const previewable =
                    (r.content_type ?? "").startsWith("image/") || r.file_name.toLowerCase().endsWith(".pdf");
                const openFile = () => {
                    // PDFs / images preview in-app; Drive-only files that
                    // can't preview open in Drive; everything else opens the
                    // dialog, which offers a graceful Download fallback.
                    if (!previewable && r.google_drive_file_id) {
                        openExternalLink(`https://drive.google.com/file/d/${r.google_drive_file_id}/view`);
                    } else {
                        setPreviewAttachment(r);
                    }
                };
                return (
                    <button
                        type="button"
                        onClick={openFile}
                        title={previewable ? "Preview" : r.file_name}
                        className="flex items-center gap-2 text-left hover:underline cursor-pointer"
                    >
                        <Paperclip className="h-3.5 w-3.5 text-foreground/80" />
                        <span className="text-sm">{r.file_name}</span>
                    </button>
                );
            },
        },
        {
            key: "category",
            header: "Type",
            sortable: true,
            sortAccessor: (r) => r.category ?? r.source,
            render: (r) => (
                <Badge variant="outline" className="text-[10px] uppercase">
                    {r.category ?? r.source}
                </Badge>
            ),
        },
        {
            key: "job_title",
            header: "Job",
            sortable: true,
            render: (r) =>
                r.job_id ? (
                    <Link to={`/jobs/${r.job_id}`} className="text-xs text-primary hover:underline">
                        {r.job_title ?? "open"}
                    </Link>
                ) : (
                    <span className="text-foreground/80 text-xs">—</span>
                ),
        },
        {
            key: "created_at",
            header: "Added",
            sortable: true,
            firstSortDir: "desc",
            sortAccessor: (r) => dateSortValue(r.created_at),
            render: (r) => <span className="text-xs tabular-nums text-foreground/80">{formatDate(r.created_at)}</span>,
        },
        {
            key: "open",
            header: "",
            render: (r) =>
                r.google_drive_file_id ? (
                    <button
                        type="button"
                        onClick={() =>
                            openExternalLink(`https://drive.google.com/file/d/${r.google_drive_file_id}/view`)
                        }
                        className="text-xs inline-flex items-center gap-1 text-primary hover:underline"
                    >
                        <ExternalLink className="h-3 w-3" /> Drive
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => setPreviewAttachment(r)}
                        className="text-xs inline-flex items-center gap-1 text-primary hover:underline"
                    >
                        <ExternalLink className="h-3 w-3" /> Open
                    </button>
                ),
        },
    ];
    // Sort every file before the 10-row preview cuts them down.
    const { sorted: sortedDocs, controlledSort: docsSort } = useSortedRows(docs, docColumns, {
        sortId: "assets.detail.files",
    });
    const visibleDocs = docsExpanded ? sortedDocs : sortedDocs.slice(0, DEFAULT_VISIBLE_ROWS);
    // Photo grid feels generous at 12 thumbnails before expanding.
    const PHOTO_PREVIEW = 12;
    const visiblePhotos = photosExpanded ? photos : photos.slice(0, PHOTO_PREVIEW);

    return (
        <div className="space-y-4">
            {photos.length > 0 && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Paperclip className="h-4 w-4" /> Photos ({photos.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {visiblePhotos.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() =>
                                        p.google_drive_file_id
                                            ? openExternalLink(
                                                  `https://drive.google.com/file/d/${p.google_drive_file_id}/view`,
                                              )
                                            : setPreviewAttachment(p)
                                    }
                                    className="relative aspect-square overflow-hidden rounded-lg border bg-muted/50 flex items-center justify-center text-xs text-foreground/80 hover:border-primary"
                                    title={photoCaption(p)}
                                >
                                    {/* Label fallback sits underneath; the image covers it
                                        when it loads, and onError hides the image to reveal it. */}
                                    <span className="px-2">{photoCaption(p)}</span>
                                    <img
                                        src={attachmentDownloadUrl(p.id, { disposition: "inline" })}
                                        alt={photoCaption(p)}
                                        loading="lazy"
                                        className="absolute inset-0 h-full w-full object-cover"
                                        onError={(e) => {
                                            e.currentTarget.style.display = "none";
                                        }}
                                    />
                                    {/* Which shot of this device, and when — never bf-test-<uuid>.jpg. */}
                                    <span className="absolute inset-x-0 bottom-0 line-clamp-2 bg-black/60 px-1.5 py-0.5 text-left text-[10px] leading-tight text-white">
                                        {photoCaption(p)}
                                    </span>
                                </button>
                            ))}
                        </div>
                        {/* `visible={PHOTO_PREVIEW}` is why the shared
                            component takes that prop: a 12-thumbnail grid is
                            not a 10-row table, and hard-coding one constant is
                            how this became a second copy in the first place. */}
                        <ShowMoreFooter
                            total={photos.length}
                            expanded={photosExpanded}
                            onToggle={() => setPhotosExpanded((v) => !v)}
                            visible={PHOTO_PREVIEW}
                        />
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Paperclip className="h-4 w-4" /> Files ({docs.length} of {total})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <DataTable
                        columns={docColumns}
                        data={visibleDocs}
                        keyField="id"
                        controlledSort={docsSort}
                        loading={q.isLoading}
                        emptyMessage="No documents attached yet."
                    />
                    <SortScopeNote loaded={rows.length} total={total} noun="attachments" />
                    <ShowMoreFooter
                        total={docs.length}
                        expanded={docsExpanded}
                        onToggle={() => setDocsExpanded((v) => !v)}
                    />
                </CardContent>
            </Card>
            <AttachmentPreviewDialog
                open={!!previewAttachment}
                onClose={() => setPreviewAttachment(null)}
                attachmentId={previewAttachment?.id ?? ""}
                fileName={
                    previewAttachment?.photo_label
                        ? previewAttachment.photo_label
                        : (previewAttachment?.file_name ?? "Attachment")
                }
            />
        </div>
    );
}

// ── Skeleton ─────────────────────────────────────────────────────────

function AssetDetailSkeleton() {
    return (
        <div className="mx-auto max-w-6xl px-4 py-6 space-y-6">
            <Skeleton className="h-4 w-48" />
            <Card>
                <CardContent className="p-5 space-y-3">
                    <Skeleton className="h-7 w-72" />
                    <Skeleton className="h-4 w-96" />
                    <div className="grid grid-cols-3 gap-3 mt-4">
                        <Skeleton className="h-14" />
                        <Skeleton className="h-14" />
                        <Skeleton className="h-14" />
                    </div>
                </CardContent>
            </Card>
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-96 w-full" />
        </div>
    );
}
